var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect } from 'react';
import DeckGL, { MapView, FlyToInterpolator } from 'deck.gl';
import cx from 'classnames';
// @ts-ignore
import { _MapContext as MapContext } from 'react-map-gl';
import { GeoJsonLayer } from '@deck.gl/layers';
import { EMapType, EMapStyle } from '../../interfaces';
import { PICKING_RADIUS, TRANSITION_DURATION, CONROLLER_2D_CONFIG, CONROLLER_3D_CONFIG, } from '../../config';
import { MapSpinner, MapCircleButton, Icons } from '../../../index';
const mapView2D = new MapView({
    controller: CONROLLER_2D_CONFIG,
});
const mapView3D = new MapView({
    controller: CONROLLER_3D_CONFIG,
});
export const DeckGLMap = props => {
    const { initialMapMode = EMapType.M2D, initialMapStyle = EMapStyle.DARK, initialViewState, newZoomLevel, colors, infringementType, tracks2D, tracks3D, geometries2D, geometries3D, showSpinner, hideToggleMode = false, hideToggleStyle = true, onStyleChange, selectedGeometries = [], resetToHome, children, setHoveredTrack, } = props;
    // Clicked data is used to show the tooltip, id being the index that was clicked.
    // id === -1 means nothing is selected.
    const [clickedData, setClickedData] = useState({
        clickedObject: null,
        x: 0,
        y: 0,
        id: -1,
    });
    const [hoveredData, setHoveredData] = useState({
        id: -1,
    });
    const [mapMode, toggleMapMode] = useState(initialMapMode);
    const [mapStyle, toggleMapStyle] = useState(initialMapStyle);
    const [isInMotion, updateIsInMotion] = useState(false);
    const [layers, setLayers] = useState([]);
    const [zoomLevel, setZoomLevel] = useState(initialViewState.zoom);
    // TODO: Figure out the minimum line widths needed to hide jagged lines.
    const calcLineWidth = (zoom) => Math.min(zoom * 10, 150);
    // TODO: Clean this up so that the infringement colour isn't necessarily reliant on the
    // isInfringement flag. Also needs to have a fallback for when the opType doesn't exist in the colour scheme.
    // It shouldn't happen, but is good practice to do so anyways.
    const getColor = (data) => {
        if (data.id === clickedData.id) {
            return colors.Selected;
        }
        else if (data.id === hoveredData.id) {
            return colors.Selected;
        }
        return data.properties.isInfringement
            ? colors.Infringement
            : colors[data.properties.operationType];
    };
    // 1. This makes it so the polygon looks transparent and the same all around.
    // 2. TODO: Extract out the colours.
    const createGeometryLayers = (geometries) => {
        if (!geometries) {
            return [new GeoJsonLayer({})];
        }
        // TODO: Will make a separate pr to remove this after testing 3d colors done
        const fillColor = typeof colors[`${infringementType}`] !== 'undefined'
            ? colors[`${infringementType}`]
            : [107, 191, 191];
        return geometries.map(data => {
            const { id: geometryId } = data;
            const id = `geometry-${geometryId}`;
            const visible = selectedGeometries.includes(Number(geometryId));
            return new GeoJsonLayer({
                id,
                data,
                visible,
                pickable: false,
                parameters: {
                    depthMask: false,
                },
                opacity: 0.2,
                stroked: mapMode === EMapType.M2D ? false : true,
                lineWidthMinPixels: 2,
                getFillColor: fillColor,
                getLineColor: fillColor,
                getLineWidth: 2,
            });
        });
    };
    // 1. The updateTriggers tell the layer to update the lineColor when clickedData changes
    // Without this, it won't update as an optimisation.
    const createTrackLayers = (tracks) => {
        if (!tracks) {
            return [new GeoJsonLayer({})];
        }
        return tracks.map((track, index) => new GeoJsonLayer({
            id: `track-${index}`,
            data: track,
            pickable: true,
            onClick: (item) => {
                setClickedData({
                    clickedObject: item.object,
                    x: item.x,
                    y: item.y,
                    id: item.object ? item.object.id : -1,
                });
            },
            onHover: (item) => {
                if (setHoveredTrack) {
                    if (item.object) {
                        setHoveredTrack(Object.assign({}, item.object, {
                            latitude: item.lngLat[1],
                            longitude: item.lngLat[0],
                        }));
                    }
                    else {
                        setHoveredTrack(null);
                    }
                }
                setHoveredData({
                    id: item.object ? item.object.id : -1,
                });
            },
            lineWidthMinPixels: 1,
            getLineColor: (d) => getColor(d),
            getLineWidth: calcLineWidth(zoomLevel),
            updateTriggers: {
                getLineColor: { clickedData, hoveredData },
            },
        }));
    };
    const setMapLayers = (mode) => {
        if (mode === EMapType.M2D) {
            setLayers([...createTrackLayers(tracks2D), ...createGeometryLayers(geometries2D)]); // add 2d layers
        }
        if (mode === EMapType.M3D) {
            setLayers([...createTrackLayers(tracks3D), ...createGeometryLayers(geometries3D)]); // add 3d layers
        }
    };
    useEffect(() => {
        if (tracks3D && tracks3D.length) {
            if (tracks3D[0].hasOwnProperty('type')) {
                setMapLayers(mapMode);
            }
        }
        else {
            setLayers([]);
        }
    }, [mapMode, zoomLevel, clickedData, hoveredData, tracks3D, geometries3D, selectedGeometries]);
    const goToHome = () => {
        const { pitch, bearing } = initialViewState, rest = __rest(initialViewState, ["pitch", "bearing"]);
        updateViewState(Object.assign({ bearing: 0, pitch: mapMode === EMapType.M2D ? 0 : 60, transitionDuration: TRANSITION_DURATION, transitionInterpolator: new FlyToInterpolator() }, rest));
    };
    useEffect(() => {
        goToHome();
    }, [resetToHome]);
    // manage view state changes
    const [mapViewState, updateViewState] = useState(initialViewState);
    useEffect(() => {
        updateViewState(initialViewState);
    }, [initialViewState]);
    // manage dynamic zoom changes
    useEffect(() => {
        const { zoom } = mapViewState, rest = __rest(mapViewState, ["zoom"]);
        if (zoom !== newZoomLevel) {
            updateViewState(Object.assign({ zoom: newZoomLevel }, rest));
        }
    }, [newZoomLevel]);
    const switchView = () => {
        // don't change view when camera is in motion
        if (isInMotion || !mapViewState) {
            return;
        }
        switchTheme();
        updateIsInMotion(true);
        setTimeout(() => {
            updateIsInMotion(false);
        }, TRANSITION_DURATION + 99);
        // get the current lat lng from the viewstate
        const { latitude: currentLatitude, longitude: currentLongitude } = mapViewState;
        if (mapMode === EMapType.M2D) {
            toggleMapMode(EMapType.M3D);
            setMapLayers(EMapType.M3D);
            const { latitude, longitude, pitch, bearing } = initialViewState, rest = __rest(initialViewState, ["latitude", "longitude", "pitch", "bearing"]);
            updateViewState(Object.assign({ latitude: currentLatitude, longitude: currentLongitude, bearing: 0, pitch: 60, transitionDuration: TRANSITION_DURATION, transitionInterpolator: new FlyToInterpolator() }, rest));
        }
        else {
            const { latitude, longitude, pitch, bearing } = initialViewState, rest = __rest(initialViewState, ["latitude", "longitude", "pitch", "bearing"]);
            updateViewState(Object.assign({ latitude: currentLatitude, longitude: currentLongitude, bearing: 0, pitch: 0, transitionDuration: TRANSITION_DURATION, transitionInterpolator: new FlyToInterpolator() }, rest));
            setTimeout(() => {
                toggleMapMode(EMapType.M2D);
                setMapLayers(EMapType.M2D);
            }, TRANSITION_DURATION);
        }
    };
    const switchTheme = () => {
        toggleMapStyle(mapStyle === EMapStyle.LIGHT ? EMapStyle.DARK : EMapStyle.LIGHT);
        if (onStyleChange) {
            onStyleChange(mapStyle);
        }
    };
    const onViewStateChange = ({ viewState }) => {
        if (mapMode === EMapType.M3D) {
            setZoomLevel(viewState.zoom);
        }
        updateViewState(viewState);
    };
    return (React.createElement("div", { className: cx('map-frame', mapStyle === EMapStyle.DARK ? 'map-mode--dark' : 'map-mode--light'), onContextMenu: event => event.preventDefault() },
        React.createElement(DeckGL, { views: mapMode === EMapType.M3D ? [mapView3D] : [mapView2D], initialViewState: mapViewState, viewState: mapViewState, onViewStateChange: onViewStateChange, 
            // @ts-ignore  -- probably wrong type?
            ContextProvider: MapContext.Provider, layers: layers, pickingRadius: PICKING_RADIUS, width: '100%', height: '100%' }, children),
        !hideToggleMode && (React.createElement(MapCircleButton, { icon: React.createElement(Icons, { iconName: `ic-ui-3d-map`, size: "18", fill: "#FFF" }), className: "mode-3d", active: mapMode === EMapType.M3D ? true : false, onClick: switchView })),
        !hideToggleStyle && (React.createElement(MapCircleButton, { icon: React.createElement(Icons, { iconName: `ic-support`, size: "18", fill: "#FFF" }), className: "dark-mode", active: false, onClick: switchTheme })),
        React.createElement(MapSpinner, { active: showSpinner })));
};
//# sourceMappingURL=DeckGLMap.js.map